import { useRef } from 'react';
import { Link } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { pluralize } from '@libs/i18n';
import { Badge } from '@ui/components/Badge';
import { CapsuleButton } from '@ui/components/CapsuleButton';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { ArrowSlimIcon } from '@ui/icons/ArrowSlimIcon';

import { useClubColors } from '@features/club/hooks/useClubColors';
import { ClubLogo } from '@features/club/ui/ClubLogo';
import { ClubDto } from '@features/shared/api/typings';

import styles from './PlayerClubs.module.css';

const MEMBERS_PLURAL = {
  one: '{number} участник',
  few: '{number} участника',
  many: '{number} участников',
  zero: '{number} участников',
};

const TOURNAMENTS_PLURAL = {
  one: '{number} турнир',
  few: '{number} турнира',
  many: '{number} турниров',
  zero: '{number} турниров',
};

type Props = {
  className?: string;
  preset: 's' | 'm' | 'l';
  clubs: ClubDto[];
};
export function PlayerClubs({ className, clubs, preset }: Props) {
  const scrollable = preset === 's';

  const clubsView = (
    <>
      {clubs.map((club) => (
        <PlayerClub key={club.clubId} club={club} preset={preset} />
      ))}
    </>
  );

  if (scrollable) {
    return (
      <HorizontalScroll
        className={cx(styles.clubs, { [styles.scrollable]: scrollable }, className)}
      >
        {clubsView}
      </HorizontalScroll>
    );
  }

  return <div className={cx(styles.clubs, className)}>{clubsView}</div>;
}

type PlayerClubProps = {
  club: ClubDto;
  preset: 's' | 'm' | 'l';
};
export function PlayerClub({ club, preset }: PlayerClubProps) {
  const ref = useRef<HTMLDivElement>(null);
  useClubColors(club.themeColor, ref);

  return (
    <Link
      className={cx(styles.club, styles[`preset_${preset}`], 'theme-dark')}
      to="/club/$clubId"
      params={{ clubId: club.clubId }}
      from="/"
    >
      <ClubLogo className={styles.logo} logoUrl={club.logoUrl} />

      <div className={styles.nameBlock} ref={ref}>
        <div className={styles.name}>{club.title}</div>

        <div className={styles.info}>
          <div className={styles.badges}>
            <Badge size="m" flat>
              {pluralize(MEMBERS_PLURAL, club.membersCount)}
            </Badge>
            <Badge size="m" flat>
              {pluralize(TOURNAMENTS_PLURAL, club.finishedTournamentsCount)}
            </Badge>
          </div>

          <CapsuleButton className={styles.button} preset="hollow-colored" size="16r-32">
            <span>Перейти в клуб</span>
            <span className={styles.arrow}>
              <ArrowSlimIcon />
            </span>
          </CapsuleButton>
        </div>
      </div>
    </Link>
  );
}
